import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Brain, CloudOff, Loader2 } from "lucide-react";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminPredictionsChrome from "../predictions/AdminPredictionsChrome";
import AdminPredictionsFiltersRail from "../predictions/AdminPredictionsFiltersRail";
import PredictionsFloorIndex from "../predictions/floor/PredictionsFloorIndex";
import PredictionsFloorDetail from "../predictions/floor/PredictionsFloorDetail";
import { fetchAdminPredictionDesk } from "../predictions/api/fetchAdminPredictions";
import {
  clientFilterPredictions,
  predictionRowId,
  sortPredictions,
} from "../predictions/constants";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../p2p/admin-p2p.css";
import "../predictions/admin-predictions.css";

const PAGE_SIZE = 15;

export default function PredictionsPage() {
  const [desk, setDesk] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminPredictionDesk({
          page: pageNum,
          limit: PAGE_SIZE,
          desk,
          search: debouncedSearch,
          refresh,
        });

        setPredictions(data?.predictions || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setPredictions([]);
        setError(err.response?.data?.error || err.message || "Failed to load Predictions desk");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [desk, debouncedSearch]
  );

  const fetchPage = useCallback(
    (pageNum, refresh = false) => {
      setPage(pageNum);
      load({ pageNum, refresh });
    },
    [load]
  );

  useEffect(() => {
    fetchPage(1);
  }, [desk, debouncedSearch, fetchPage]);

  const handleRefresh = () => load({ pageNum: page, refresh: true });

  const handlePageChange = (nextPage) => {
    if (nextPage === page || nextPage < 1) return;
    fetchPage(nextPage);
  };

  const displayPredictions = useMemo(() => {
    let list = clientFilterPredictions(predictions, search);
    return sortPredictions(list, sortKey);
  }, [predictions, search, sortKey]);

  const selectedPrediction = useMemo(() => {
    if (!selectedId) return null;
    return (
      displayPredictions.find((row) => predictionRowId(row) === selectedId) ||
      predictions.find((row) => predictionRowId(row) === selectedId) ||
      null
    );
  }, [displayPredictions, predictions, selectedId]);

  useEffect(() => {
    if (!displayPredictions.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible =
      selectedId && displayPredictions.some((row) => predictionRowId(row) === selectedId);
    if (!stillVisible) {
      setSelectedId(predictionRowId(displayPredictions[0]));
    }
  }, [displayPredictions, selectedId]);

  const activeFilterCount = [desk !== "all", sortKey !== "newest"].filter(Boolean).length;

  const handleClearFilters = () => {
    setDesk("all");
    setSortKey("newest");
    setSearch("");
  };

  const railProps = {
    desk,
    sortKey,
    summary,
    resultCount: displayPredictions.length,
    onDeskChange: setDesk,
    onSortChange: setSortKey,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const isLoading = loading && !refreshing && predictions.length === 0;

  return (
    <div className="am-page apr-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a apr-aurora" />
      </div>

      <AdminPredictionsChrome
        search={search}
        onSearchChange={setSearch}
        loading={loading || refreshing}
        onRefresh={handleRefresh}
        filtersOpen={filtersOpen}
        onFiltersToggle={() => setFiltersOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        summary={summary}
        totalLoaded={pagination.total || predictions.length}
      />

      <AdminDesk
        sectionKey="predictions"
        sidebarLabel="Predictions filters"
        sidebar={<AdminPredictionsFiltersRail {...railProps} />}
      >
            {error ? (
              <div className="apr-error-banner" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)}>
                  Dismiss
                </button>
              </div>
            ) : null}

            <div className="am-board am-board-floor">
              {isLoading ? (
                <div className="am-state">
                  <Loader2 size={28} className="am-spin" />
                  <p>Loading fixture predictions…</p>
                </div>
              ) : error && !predictions.length ? (
                <div className="am-state">
                  <CloudOff size={28} />
                  <p>{error}</p>
                  <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleRefresh}>
                    Try again
                  </button>
                </div>
              ) : displayPredictions.length === 0 && !loading ? (
                <div className="am-state">
                  <Brain size={28} />
                  <h2>No predictions match</h2>
                  <p>
                    {desk === "upcoming"
                      ? "Nothing scheduled on upcoming fixtures."
                      : "Try clearing filters or adjusting your search."}
                  </p>
                  {activeFilterCount || search ? (
                    <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleClearFilters}>
                      Clear filters
                    </button>
                  ) : null}
                </div>
              ) : (
                <AdminFloorGrid
                  sectionKey="predictions"
                  index={
                    <PredictionsFloorIndex
                      predictions={displayPredictions}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      desk={desk}
                      pagination={pagination}
                      loading={loading}
                      onPageChange={handlePageChange}
                    />
                  }
                  detail={<PredictionsFloorDetail prediction={selectedPrediction} />}
                />
              )}
            </div>
      </AdminDesk>

      {filtersOpen ? (
        <div className="am-filters-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
          <div className="am-filters-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Predictions filters">
            <AdminPredictionsFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
